import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { 
  Folder, 
  File, 
  ArrowLeft, 
  Loader2, 
  FolderOpen,
  ExternalLink,
  Settings,
  Search,
  AlertCircle
} from 'lucide-react';
import { 
  getDriveStatus, 
  listDriveFiles, 
  getDriveConfig,
  saveDriveConfig,
  searchDriveFiles
} from '../services/apiService';
import { cn } from '../lib/utils';

const RepositorioDocumentos = () => {
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveEmail, setDriveEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [currentFolderName, setCurrentFolderName] = useState('Mi Drive');
  const [folderHistory, setFolderHistory] = useState([]);
  const [rootFolderId, setRootFolderId] = useState(null);
  const [rootFolderName, setRootFolderName] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [searchNextPageToken, setSearchNextPageToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadDriveStatus = useCallback(async () => {
    try {
      const response = await getDriveStatus();
      if (response.success) {
        setDriveConnected(response.connected);
        setDriveEmail(response.email);
        if (response.rootFolderConfigured) {
          setRootFolderId(response.rootFolderId);
          setRootFolderName(response.rootFolderName);
          // Si hay carpeta raíz configurada, empezar desde ahí
          if (response.rootFolderId && folderHistory.length === 0) {
            setCurrentFolderId(response.rootFolderId);
            setCurrentFolderName(response.rootFolderName);
          }
        }
      }
    } catch (error) {
      console.error('Error cargando estado de Drive:', error);
    } finally {
      setLoading(false);
    }
  }, [folderHistory.length]);

  const loadDriveConfig = useCallback(async () => {
    try {
      const response = await getDriveConfig();
      if (response.success && response.config) {
        setRootFolderId(response.config.root_folder_id);
        setRootFolderName(response.config.root_folder_name);
        if (response.config.root_folder_id && folderHistory.length === 0) {
          setCurrentFolderId(response.config.root_folder_id);
          setCurrentFolderName(response.config.root_folder_name);
        }
      }
    } catch (error) {
      console.error('Error cargando configuración de Drive:', error);
    }
  }, [folderHistory.length]);

  // Cargar estado de Drive y configuración
  useEffect(() => {
    loadDriveStatus();
    loadDriveConfig();
  }, [loadDriveStatus, loadDriveConfig]);

  // Cargar archivos cuando cambia la carpeta actual (solo si no estamos en modo búsqueda)
  useEffect(() => {
    if (driveConnected && currentFolderId && !isSearchMode) {
      loadFiles(currentFolderId);
    }
  }, [driveConnected, currentFolderId, isSearchMode]);

  const loadFiles = async (folderId, pageToken = null, append = false) => {
    if (!append) {
      setLoadingFiles(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const response = await listDriveFiles(folderId, pageToken);
      if (response.success) {
        // Separar carpetas y archivos, ordenar carpetas primero
        const folders = (response.files || []).filter(f => f.mimeType === 'application/vnd.google-apps.folder');
        const filesList = (response.files || []).filter(f => f.mimeType !== 'application/vnd.google-apps.folder');
        
        const newFiles = [...folders, ...filesList];
        
        if (append) {
          setFiles(prev => [...prev, ...newFiles]);
        } else {
          setFiles(newFiles);
        }
        
        setNextPageToken(response.nextPageToken || null);
      }
    } catch (error) {
      console.error('Error cargando archivos:', error);
    } finally {
      setLoadingFiles(false);
      setLoadingMore(false);
    }
  };

  const loadMoreFiles = () => {
    if (nextPageToken && !loadingMore) {
      loadFiles(currentFolderId, nextPageToken, true);
    }
  };

  const handleFolderClick = async (folder) => {
    // Agregar carpeta actual al historial
    setFolderHistory(prev => [...prev, {
      id: currentFolderId,
      name: currentFolderName
    }]);
    
    setCurrentFolderId(folder.id);
    setCurrentFolderName(folder.name);
  };

  const handleBack = () => {
    if (folderHistory.length > 0) {
      const previousFolder = folderHistory[folderHistory.length - 1];
      setFolderHistory(prev => prev.slice(0, -1));
      setCurrentFolderId(previousFolder.id);
      setCurrentFolderName(previousFolder.name);
    } else if (rootFolderId && currentFolderId !== 'root') {
      // Si estamos en la carpeta raíz configurada, volver a root
      setCurrentFolderId('root');
      setCurrentFolderName('Mi Drive');
    }
  };

  const handleSearch = async (pageToken = null, append = false) => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearchMode(false);
      // Si estaba en modo búsqueda, volver a cargar archivos normales
      if (isSearchMode) {
        loadFiles(currentFolderId);
      }
      return;
    }

    if (!append) {
      setSearching(true);
    } else {
      setLoadingMore(true);
    }
    
    setIsSearchMode(true);
    
    try {
      const response = await searchDriveFiles(searchQuery, pageToken);
      if (response.success) {
        const newResults = response.files || [];
        
        if (append) {
          setSearchResults(prev => [...prev, ...newResults]);
        } else {
          setSearchResults(newResults);
        }
        
        setSearchNextPageToken(response.nextPageToken || null);
      }
    } catch (error) {
      console.error('Error buscando en Drive:', error);
    } finally {
      setSearching(false);
      setLoadingMore(false);
    }
  };

  const loadMoreSearchResults = () => {
    if (searchNextPageToken && !loadingMore) {
      handleSearch(searchNextPageToken, true);
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!value.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      // Recargar archivos normales si salimos del modo búsqueda
      if (isSearchMode && driveConnected && currentFolderId) {
        loadFiles(currentFolderId);
      }
    }
  };

  const handleSelectRootFolder = async (folderId, folderName) => {
    try {
      const response = await saveDriveConfig(folderId, folderName);
      if (response.success) {
        setRootFolderId(folderId);
        setRootFolderName(folderName);
        setCurrentFolderId(folderId);
        setCurrentFolderName(folderName);
        setFolderHistory([]);
        setSearchQuery('');
        setSearchResults([]);
        // Recargar archivos
        loadFiles(folderId);
      }
    } catch (error) {
      console.error('Error guardando configuración:', error);
      alert('Error al guardar la configuración');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isFolder = (mimeType) => {
    return mimeType === 'application/vnd.google-apps.folder';
  };

  const openFile = (file) => {
    if (file.webViewLink) {
      window.open(file.webViewLink, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Repositorio de Documentos</h1>
          <p className="text-muted-foreground mt-2">
            Navega por tus documentos de Google Drive
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Cargando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!driveConnected) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Repositorio de Documentos</h1>
          <p className="text-muted-foreground mt-2">
            Navega por tus documentos de Google Drive
          </p>
        </div>
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Drive no conectado</h3>
            <p className="text-muted-foreground mb-4">
              Para usar el repositorio de documentos, necesitas conectar tu cuenta de Google Drive.
            </p>
            <Button onClick={() => window.location.href = '/#/configuracion/integraciones'}>
              <Settings className="h-4 w-4 mr-2" />
              Ir a Configuración
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Repositorio de Documentos</h1>
          <p className="text-muted-foreground mt-2">
            {driveEmail && `Conectado como ${driveEmail}`}
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => window.location.href = '/#/configuracion/integraciones'}
        >
          <Settings className="h-4 w-4 mr-2" />
          Configurar Carpeta Raíz
        </Button>
      </div>

      {/* Configuración de carpeta raíz */}
      {!rootFolderId && (
        <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configurar Carpeta Raíz
            </CardTitle>
            <CardDescription>
              Selecciona la carpeta de Drive que quieres usar como repositorio de documentos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Buscar carpeta por nombre..."
                className="flex-1 p-2 rounded-md border border-input bg-background"
              />
              <Button onClick={() => handleSearch()} disabled={searching}>
                {searching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Resultados de búsqueda:</p>
                {searchResults.map((folder) => (
                  <Button
                    key={folder.id}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleSelectRootFolder(folder.id, folder.name)}
                  >
                    <Folder className="h-4 w-4 mr-2" />
                    {folder.name}
                  </Button>
                ))}
              </div>
            )}
            <Button
              variant="outline"
              onClick={() => handleSelectRootFolder('root', 'Mi Drive')}
            >
              Usar "Mi Drive" como carpeta raíz
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Barra de búsqueda global */}
      {rootFolderId && (
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Buscar archivos y carpetas en todo Drive..."
                  className="w-full pl-10 p-2 rounded-md border border-input bg-background"
                />
              </div>
              <Button onClick={() => handleSearch()} disabled={searching || !searchQuery.trim()}>
                {searching ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Buscando...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Buscar
                  </>
                )}
              </Button>
              {isSearchMode && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    setIsSearchMode(false);
                    setSearchResults([]);
                    loadFiles(currentFolderId);
                  }}
                >
                  Cancelar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navegación */}
      {!isSearchMode && (
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {(folderHistory.length > 0 || (rootFolderId && currentFolderId !== 'root')) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBack}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Atrás
                  </Button>
                )}
                <CardTitle className="flex items-center gap-2">
                  <FolderOpen className="h-5 w-5" />
                  {currentFolderName}
                </CardTitle>
              </div>
              {rootFolderId && (
                <div className="text-sm text-muted-foreground">
                  Carpeta raíz: {rootFolderName}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingFiles ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Folder className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Esta carpeta está vacía</p>
              </div>
            ) : (
              <>
                <div className="grid gap-2">
                  {files.map((file) => (
                    <div
                      key={file.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer",
                        isFolder(file.mimeType) && "hover:border-primary/50"
                      )}
                      onClick={() => isFolder(file.mimeType) ? handleFolderClick(file) : openFile(file)}
                    >
                      <div className="flex-shrink-0">
                        {isFolder(file.mimeType) ? (
                          <Folder className="h-5 w-5 text-blue-500" />
                        ) : (
                          <File className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{file.name}</p>
                        {!isFolder(file.mimeType) && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                            {file.size && <span>{formatFileSize(parseInt(file.size))}</span>}
                            {file.modifiedTime && (
                              <>
                                {file.size && <span>•</span>}
                                <span>{formatDate(file.modifiedTime)}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {file.webViewLink && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openFile(file);
                          }}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                {nextPageToken && (
                  <div className="mt-4 text-center">
                    <Button
                      variant="outline"
                      onClick={loadMoreFiles}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Cargando...
                        </>
                      ) : (
                        'Cargar más'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Resultados de búsqueda */}
      {isSearchMode && (
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Resultados de búsqueda: "{searchQuery}"
            </CardTitle>
            <CardDescription>
              {searchResults.length > 0 
                ? `${searchResults.length} resultado${searchResults.length !== 1 ? 's' : ''} encontrado${searchResults.length !== 1 ? 's' : ''}`
                : 'No se encontraron resultados'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {searching && searchResults.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No se encontraron archivos o carpetas</p>
              </div>
            ) : (
              <>
                <div className="grid gap-2">
                  {searchResults.map((file) => (
                    <div
                      key={file.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer",
                        isFolder(file.mimeType) && "hover:border-primary/50"
                      )}
                      onClick={() => {
                        if (isFolder(file.mimeType)) {
                          setIsSearchMode(false);
                          setSearchQuery('');
                          setSearchResults([]);
                          handleFolderClick(file);
                        } else {
                          openFile(file);
                        }
                      }}
                    >
                      <div className="flex-shrink-0">
                        {isFolder(file.mimeType) ? (
                          <Folder className="h-5 w-5 text-blue-500" />
                        ) : (
                          <File className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{file.name}</p>
                        {!isFolder(file.mimeType) && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                            {file.size && <span>{formatFileSize(parseInt(file.size))}</span>}
                            {file.modifiedTime && (
                              <>
                                {file.size && <span>•</span>}
                                <span>{formatDate(file.modifiedTime)}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {file.webViewLink && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openFile(file);
                          }}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                {searchNextPageToken && (
                  <div className="mt-4 text-center">
                    <Button
                      variant="outline"
                      onClick={loadMoreSearchResults}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Cargando...
                        </>
                      ) : (
                        'Cargar más resultados'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RepositorioDocumentos;
